// Hand-transcribed from "[NEW]Responding to AI-Era Threat Cisco Branding.pdf"
// (2026-10-01), the ACME Global Enterprises reference report — 10 sections:
// Executive Summary, Key Workshop Observations and Priority Areas, Executive
// Next Steps and 30-60-90 Day Execution Roadmap, Cisco Talos Intelligence, and
// Appendices 1-6. Every section's prose, tables, and figures are written to
// match that PDF (and its companion .docx — same source file) exactly, using
// the engine's existing text vocabulary (modules/report/blocks.ts): priority
// cards, ranked/numbered lists, stats strips, and heading-anchored bundled
// diagrams (modules/report/bundledDiagrams.ts). Bracketed fields that already
// auto-fill from captured workshop data ([Customer Company Name], [Quantity/
// type], [current sources], [HQ/Branch]) are reused as-is rather than
// hardcoded, so this still renders correctly for any campaign/customer.

// The authored-template vocabulary is campaign-agnostic and lives in the engine.
// This file is the Frontier AI campaign's data for it: the actual sections,
// imported from the Word template by scripts/import-report-template.mjs.
import type { TemplateBlock, TemplateSection } from '../modules/report/types';
export type { TemplateBlock, TemplateSection } from '../modules/report/types';

/**
 * Sections a report starts without.
 *
 * `post-workshop-action`, `post-workshop-recommendations`, `resources-tool-directory`
 * (the 30-60-90 roadmap, Cisco Talos, and the appendix) and, as of 2026-09-23,
 * `recommended-immediate-next` came off this list alongside widening
 * `ACTIVE_REPORT_TEMPLATE` to include them: leaving a section here would mean a
 * brand-new workshop's report starts with it hidden, so an SE would have to know
 * to un-hide it before the reference design's full shape shows up. This list is
 * only consulted at a workshop's first-seed (see `api/emptyState.ts`) — an
 * already-existing workshop's own saved `reportLayout.hidden` is never touched by
 * this change, so a workshop seeded before this section was un-hidden still needs
 * it un-hidden by hand (the "Leave out"/"Include" toggle in the report editor).
 *
 * `recommended-plays-full`/`findings-method` referred to sections that were
 * generated from the deep-dive and are not currently built at all (see
 * modules/export/report.ts) — left in place for when the report's fuller shape
 * returns.
 */
export const DEFAULT_HIDDEN_SECTIONS = ['recommended-plays-full', 'findings-method'];

/** The document's own header lines, above section 1. */
export const TEMPLATE_INTRO: TemplateBlock[] = [
  {
    "kind": "h1",
    "text": "Responding to AI-Era Cyber Threats"
  },
  {
    "kind": "h2",
    "text": "Post Workshop Report and Action Plan"
  },
  {
    "kind": "p",
    "lead": "Prepared for:",
    "text": "[Customer Company Name]"
  },
  {
    "kind": "p",
    "lead": "Date:",
    "text": "[Date]"
  },
  {
    "kind": "p",
    "lead": "Prepared by:",
    "text": "[Name/Team]"
  },
  {
    "kind": "p",
    "lead": "Workshop initiative:",
    "text": "Preparing to respond to AI-era cyber threats and strengthening organizational resilience"
  },
  {
    "kind": "p",
    "lead": "Classification:",
    "text": "Cisco Confidential"
  },
  {
    "kind": "p",
    "text": "This report is intended for use by authorized Cisco partners and customer teams in connection with relevant services for Cisco customers. Distribution should follow all applicable confidentiality requirements."
  }
];

export const REPORT_TEMPLATE: TemplateSection[] = [
  {
    "id": "executive-summary",
    "number": 1,
    "title": "Executive Summary",
    "blocks": [
      {
        "kind": "p",
        "text": "The cybersecurity landscape has crossed a critical threshold. The rapid emergence of Frontier AI models, autonomous agent frameworks, and machine-speed exploit synthesis has fundamentally altered the velocity, economics, and scale of cyber-attacks. Adversaries now operate on machine speed; they leverage generative and agentic AI to autonomously discover vulnerabilities, synthesize weaponized functional exploits, and execute complex lateral attack paths in hours rather than weeks."
      },
      {
        "kind": "h3",
        "text": "Macro Threat Landscape"
      },
      {
        "kind": "p",
        "text": "Why resilience now requires continuous, automated defense rather than periodic assessment."
      },
      {
        "kind": "p",
        "text": "The Velocity Gap: Redefining Resilience Against AI-Accelerated Threats"
      },
      {
        "kind": "p",
        "text": "This shift increases the importance of maintaining current asset, software, identity, application, and AI workload inventories; understanding whether vulnerable systems are reachable and exploitable; prioritizing remediation according to exposure and business impact; establishing predictable patching and upgrade processes; applying compensating controls when permanent fixes cannot be implemented immediately; reducing lateral movement through Zero Trust and segmentation; and continuously monitoring lifecycle status, exposure, control effectiveness, and operational drift."
      },
      {
        "kind": "p",
        "text": "During the workshop, [Customer Company Name] and the workshop team reviewed the organization's infrastructure, lifecycle posture, identity controls, remote-access architecture, segmentation, vulnerability-management practices, AI workloads, and security operations. The workshop findings were evaluated using impact, urgency, feasibility, and expected risk reduction to identify the areas requiring the most immediate attention."
      },
      {
        "kind": "p",
        "text": "This document presents the resulting findings, priority recommendations, and a practical roadmap for completing the recommended actions. Readers will review the real-world observations supporting each recommendation, understand the associated business and security impacts, and identify the immediate and longer-term actions required to improve resilience. [Customer Company Name] can use this document as an executive decision-making reference and as an implementation guide for planning, prioritizing, funding, assigning ownership, and tracking remediation activities."
      },
      {
        "kind": "p",
        "text": "The Priority Action Summary presents the highest-weighted workshop findings, including each observed condition, its potential impact, the immediate action required, and the expected outcome. The sections that follow provide supporting evidence, recommended capabilities and services, implementation considerations, and a phased roadmap for improving the organization's security posture."
      },
      {
        "kind": "h3",
        "text": "Road to Resilience"
      },
      {
        "kind": "p",
        "text": "Three moves change the trajectory, each converts a standing exposure into a controlled, measurable outcome."
      },
      {
        "kind": "p",
        "text": "The Modern Security Blueprint: Transforming Exposure into Measurable Control"
      },
      {
        "kind": "p",
        "text": "> Note: The resulting plan is intended to help [Customer Company Name] reduce immediate threat exposure while establishing a sustainable operating model for unified asset visibility, Zero Trust segmentation, automated lifecycle modernization, AI workload defense, and continuous resilience."
      },
      {
        "kind": "h3",
        "text": "Top Weighted Recommendations"
      },
      {
        "kind": "p",
        "text": "Top-weighted recommendations from the workshop:\n1. Modernize High-Risk & Unsupported Infrastructure\nEstablish a funded, risk-prioritized refresh roadmap to retire 40+ LDoS asset sub-categories across enterprise data centers, campus, and branch sites. Immediately apply temporary compensating controls (device isolation, restricted management access, Snort IPS inspection) on high-exposure internet-facing assets.\n2. Unify Estate Visibility & Lifecycle Intelligence with Cisco IQ\nConsolidate fragmented asset inventory across CMDB, Catalyst Center, Catalyst SD-WAN Manager, Nexus Dashboard, and Meraki into Cisco IQ. Deploy Cisco IQ Link to establish an automated, real-time single pane of glass for PSIRT advisories, Field Notices, and LDoS milestone tracking.\n3. Enforce Zero Trust Microsegmentation in Production (OT/IoT)\nDeploy Cisco Secure Firewall with Snort 3 IPS and Cisco Secure Network Analytics (NetFlow/telemetry) to segment the flat production floor into dedicated production, IoT, management, and shared-services zones, containing lateral threat propagation without operational disruption.\n4. Accelerate Execution via Resilient Infrastructure Services\nEngage Cisco Resilient Infrastructure Services and Professional Services to de-risk multi-technology modernization. Leverage the Automated Validation Framework (AVF) and Services as Code to execute pre-change validation, automated rollbacks, and zero-downtime upgrades.\n5. Eliminate Shadow AI & Secure Frontier AI Workloads with Cisco AI Defense\nConduct an enterprise-wide AI discovery inventory and deploy Cisco AI Defense alongside Cisco Secure Access (Universal ZTNA) and Duo Security to enforce runtime LLM guardrails, prevent prompt injection/data leakage, and scope autonomous agent permissions with automated kill-switches."
      }
    ]
  },
  {
    "id": "key-workshop-findings",
    "number": 2,
    "title": "Key Workshop Observations and Priority Areas",
    "blocks": [
      {
        "kind": "p",
        "text": "Based on our workshop findings, we have developed a prescriptive, prioritized plan to help [Customer Company Name] strengthen its security posture against AI-era threats. The plan addresses the most significant lifecycle, exposure, visibility, segmentation, operational, and AI-security risks identified during the workshop, with concrete actions and target timelines."
      },
      {
        "kind": "h3",
        "text": "Priority Action Summary"
      },
      {
        "kind": "p",
        "text": "Five priority areas, with the immediate and near-term actions required for each."
      },
      {
        "kind": "p",
        "text": "Priority 1: Modernizing High-Risk & Unsupported Infrastructure\nObservation: [Quantity/type] contain assets past or approaching Last Day of Support (LDoS) affected by validated Cisco security advisories, including CISA Known Exploited Vulnerabilities (KEV). Lacking vendor security fixes, these devices represent persistent exposure across data centers, campus, and branch locations.\nImmediate (0-30 days): Confirm affected assets, running software releases, reachability, and critical business dependencies. Implement temporary compensating controls (device isolation, restricted management access, disabling unnecessary services, firewall/Snort IPS inspection, heightened monitoring) on internet-facing and sensitive infrastructure.\nNear-term (31-90 days): Approve a phased, risk-prioritized refresh plan and begin replacing the highest-risk assets within 90 days. Schedule and execute remaining hardware and software refresh activities over a 3-12 month window aligned with operational maintenance windows.\nCisco Products, Solutions & Services: Cisco Resilient Infrastructure Services (Modernization Track), Cisco Secure Firewall with Snort 3 IPS, Cisco Technical Assistance Center (TAC). For more information refer to: Appendix A.2 & A.4\nTarget outcome: Retire high-exposure unsupported infrastructure, enforce validated compensating controls during transition, and establish a funded, predictable lifecycle modernization process."
      },
      {
        "kind": "p",
        "text": "Priority 2: Unify Estate Visibility & Lifecycle Intelligence with Cisco IQ\nObservation: Cisco inventory, software, and vulnerability data is fragmented across [current sources], delaying security advisory impact analysis and obscuring support coverage and remediation tracking.\nImmediate (0-30 days): Establish Cisco IQ as the single consolidated source for Cisco asset and lifecycle intelligence. Create or migrate the customer account, add eligible support contracts and users, and connect available cloud-managed controllers.\nNear-term (31-90 days): Deploy Cisco IQ Link for on-prem discovery across campus, WAN, and data center domains. Evaluate Cisco Cloud Control for cross-domain topology, telemetry, and operational context. Establish a recurring 90-day review covering PSIRT advisories, Field Notices, software baselines, and LDoS milestones.\nCisco Products, Solutions & Services: Cisco IQ, Cisco IQ Link, Cisco Cloud Control, Cisco Catalyst Center, Cisco Nexus Dashboard, Meraki Dashboard. For more information refer to: Appendix A.3\nTarget outcome: Achieve a single, real-time source of truth for all Cisco assets, advisories, telemetry, and lifecycle status, significantly accelerating vulnerability identification and remediation velocity."
      },
      {
        "kind": "p",
        "text": "Priority 3: Enforce Zero Trust Microsegmentation in Production (OT/IoT)\nObservation: [HQ/Branch] production floors contain Industrial Ethernet switches, IoT/OT systems, and manufacturing applications with constrained maintenance windows. The flat network design permits broad communication paths and risks lateral threat movement into critical production services.\nImmediate (0-30 days): Enable and validate flow telemetry to map required communication paths between production systems, users, applications, and management services. Define dedicated security zones (production, IoT, management, shared services) and identify unauthorized lateral paths.\nNear-term (31-90 days): Deploy Cisco Secure Firewall at ingress, egress, and internal segmentation points (monitor mode transitioning to active enforcement). Enforce Snort IPS and encrypted traffic visibility. Deploy Cisco Secure Network Analytics for flow validation as the first phase of a Hybrid Mesh Firewall architecture.\nCisco Products, Solutions & Services: Cisco Secure Firewall (Threat Defense), Snort 3 IPS, Cisco Secure Network Analytics, Cisco Identity Services Engine (ISE). For more information refer to: Appendix A.1 & A.2\nTarget outcome: Enforce Zero Trust microsegmentation, contain lateral attack propagation, and protect sensitive manufacturing/OT operations without disrupting business continuity."
      },
      {
        "kind": "p",
        "text": "Priority 4: Accelerate Execution via Resilient Infrastructure Services\nObservation: Required refresh, OS upgrades, hardening, and segmentation span complex multi-technology domains and must be executed without disrupting 24/7 business operations, requiring specialized delivery capacity, automated validation, and structured program management.\nImmediate (0-30 days): Engage Cisco Resilient Infrastructure Services or Professional Services for an exposure and modernization planning session to validate affected assets, attack paths, temporary controls, target software baselines, and replacement priorities.\nNear-term (31-90 days): Produce a comprehensive phased implementation plan (sequencing, segmentation, pre-change validation, rollback, post-change assurance). Launch the first implementation wave focused on highest-exposure assets using the Automated Validation Framework and Services as Code.\nCisco Products, Solutions & Services: Cisco Resilient Infrastructure Services (Defense Resiliency Track), Cisco Professional Services, Automated Validation Framework (AVF), Services as Code. For more information refer to: Appendix A.4\nTarget outcome: Execute infrastructure modernization safely and predictably through automated validation and expert delivery, accelerating remediation while maintaining business continuity."
      },
      {
        "kind": "p",
        "text": "Priority 5: Eliminate Shadow AI & Secure Frontier AI Workloads with Cisco AI Defense\nObservation: Pervasive adoption and planned use of LLMs, AI applications, coding assistants, SaaS copilots, and autonomous agents across business units without centralized visibility creates Shadow AI exposure, prompt injection vulnerabilities, data leakage, and unmonitored agent permissions.\nImmediate (0-30 days): Build a comprehensive inventory of all AI applications, models, agents, coding assistants, Model Context Protocol (MCP) servers, and SaaS AI services — capturing owner, purpose, hosting, model version, data classification, tool access, and privileges. Flag unapproved and orphan workloads.\nNear-term (31-90 days): Conduct security assessments on priority AI workloads (prompt injection, jailbreaks, data leakage, tool access). Deploy Cisco AI Defense for runtime guardrails, prompt/data protection, behavioral monitoring, agent kill-switches, and mandatory human approval for high-consequence actions, backed by Cisco Secure Access, Duo, and Splunk.\nCisco Products, Solutions & Services: Cisco AI Defense, Cisco Secure Access (Universal ZTNA), Duo Security (Continuous Identity), Cisco Identity Intelligence, Splunk Enterprise Security. For more information refer to: Appendix A.1 & A.5\nTarget outcome: Establish robust enterprise AI governance, protect proprietary data from model leakage, defend against prompt injection and agent abuse, and secure the AI lifecycle end-to-end."
      }
    ]
  },
  {
    "id": "post-workshop-action",
    "number": 3,
    "title": "Executive Next Steps and 30-60-90 Day Execution Roadmap",
    "blocks": [
      {
        "kind": "p",
        "text": "To operationalize the workshop findings, the Cisco Account Team, Security Systems Engineering, and Services Leadership propose the following structured engagement governance to achieve immediate risk reduction and build long-term security resilience."
      },
      {
        "kind": "p",
        "text": "Recommended Immediate Actions — Next 14 Days:\n1. Executive Sponsor Alignment\nAssign executive accountable leads for Infrastructure Modernization, Zero Trust, and AI Governance.\n2. Cisco IQ Onboarding\nComplete the Cisco IQ tenant migration and deploy Cisco IQ Link for Catalyst, Nexus, and Meraki environments.\n3. Headquarters and Branch Segmentation Workshop\nConvene a joint Cisco and OT engineering session to review flow telemetry and define firewall security zones.\n4. AI Workload Discovery Kickoff\nInitiate Shadow AI discovery using Cisco AI Defense across development teams and enterprise SaaS."
      },
      {
        "kind": "p",
        "text": "Phased milestones, deliverables, and accountability."
      },
      {
        "kind": "table",
        "rows": [
          [
            "Timeframe",
            "Focus Area",
            "Milestones & Deliverables",
            "Accountability"
          ],
          [
            "0-30 days (Tactical)",
            "Visibility, isolation & governance",
            "Onboard Cisco IQ and Cisco IQ Link; apply compensating controls on high-risk LDoS assets; baseline production flow telemetry; inventory all active AI applications and models.",
            "Customer Security Lead + Cisco Security SE"
          ],
          [
            "31-60 days (Operational)",
            "Segmentation & architecture",
            "Approve multi-year hardware refresh plan; define microsegmentation zones for production plant; perform security assessment on top-priority AI app; engage Cisco Resilient Infrastructure Services.",
            "Customer VP of IT + Cisco Services"
          ],
          [
            "61-90 days (Strategic)",
            "Enforcement & automation",
            "Begin Wave 1 hardware replacement; transition Cisco Secure Firewall to active enforcement; implement Cisco AI Defense runtime guardrails; establish quarterly PSIRT and LDoS review cadence.",
            "CISO / CTO Steering Committee"
          ]
        ]
      }
    ]
  },
  {
    "id": "post-workshop-recommendations",
    "number": 4,
    "title": "Cisco Talos Intelligence: Real World Findings and AI Era Defensive Response",
    "blocks": [
      {
        "kind": "p",
        "text": "Cisco Talos is not included simply as a telemetry source. Its value is that Talos identifies real-world exploitation patterns, reverse-engineers attacker tooling, publishes vulnerability research, and turns those findings into practical protections across Cisco Secure Firewall, Snort, Cisco Secure Access, Cisco XDR/Splunk telemetry, and Cisco IQ lifecycle intelligence. In the Frontier AI era, this matters because threat actors can use agentic workflows and AI-assisted analysis to compress reconnaissance, exploit generation, and lateral movement decisions from days or months into hours."
      },
      {
        "kind": "p",
        "text": "Stats:\n550B+ | Security events processed daily\n20B | Malicious encounters blocked daily\n#1 | Commercial threat intelligence team"
      },
      {
        "kind": "p",
        "text": "Real-world Talos findings shaping the AI-era defensive response:\n1. CLOSEDQUORUM: The First Reported Autonomous AI C2 Implant\nTalos documented CLOSEDQUORUM as a 64-bit Windows implant written in Go that replaces conventional command-and-control infrastructure with autonomous decision-making through commercial large language models. The implant collects device context and queries multiple LLM providers, including DeepSeek, Qwen, Mistral, and Google Gemini, then uses a plurality-style decision process to choose actions such as credential theft, cryptocurrency theft, or evasion behavior. **Customer Relevance:** Talos notes this sample represents an architectural shift even though in-the-wild deployment has not been confirmed. If this pattern becomes operational at scale, malware no longer waits for a human operator to inspect every compromised system and decide the next move. It can make environment-aware decisions in minutes, reducing the defender's containment window from days to hours.\n2. CAIRN: Hunting AI-Integrated Malware at Scale\nTalos introduced CAIRN — the Cognitive Artifact Intelligence Research Network — to track the fingerprints that AI-integrated malware leaves behind. Instead of relying only on conventional signatures, CAIRN looks for cognitive artifacts such as prompt templates, LLM provider endpoints, orchestration logic, jailbreak terminology, embedded API references, and AI framework traces exposed in metadata. **Customer Relevance:** CAIRN gives defenders a repeatable way to identify early AI-enabled malware patterns before they become widespread campaigns. This moves detection upstream from waiting for obvious compromise behavior to finding AI orchestration indicators while the tooling is still emerging.\n3. ArcaneDoor: Real-World Exploitation of Perimeter Network Devices\nTalos' ArcaneDoor investigation identified a sophisticated campaign tracked as UAT4356, also tracked by Microsoft as STORM-1849, targeting Cisco Adaptive Security Appliance environments. The campaign used backdoors named LINE RUNNER and LINE DANCER and involved CVE-2024-20353 and CVE-2024-20359. **Customer Relevance:** Perimeter infrastructure gives an attacker a privileged vantage point into enterprise traffic. In ArcaneDoor, affected victims, including government networks, faced unauthorized configuration modification, reconnaissance, traffic exfiltration, and potential lateral movement. This is why lifecycle modernization, rapid patching, MFA, forensic readiness, and segmentation are required controls.\n4. Vulnerability-to-Protection Pipeline: From Talos Finding to Customer Control\nTalos vulnerability researchers discover and disclose vulnerabilities, publish technical analysis, and map many findings to deployable Snort detections. Snort coverage turns research into prevention logic for Cisco Secure Firewall and other enforcement points. Cisco customers can use Talos vulnerability reports and Snort rule documentation to map CVEs to available protections and verify coverage. **Customer Relevance:** The value is time compression for defenders. If adversaries can analyze patches and weaponize findings in hours, customers need telemetry, signatures, indicators, firewall protections, and prioritized remediation immediately — not after a manual spreadsheet review cycle.\n5. Cisco's Practical Defensive Approach for AI-Era Exploitation\nCisco's approach combines Talos intelligence with layered controls: Snort 3 IPS for network exploit prevention; Secure Firewall and Secure Access for policy enforcement; Duo and Identity Intelligence for continuous trust decisions; Cisco XDR/Splunk telemetry for incident correlation; Cisco IQ for estate-level PSIRT, advisory, and lifecycle prioritization; and Cisco AI Defense for runtime protection of enterprise AI applications and autonomous agent workflows. **Customer Relevance:** This turns Talos research into an operating model — identify the exploit pattern, block known activity, correlate exposure to exact assets and software versions, prioritize patching based on active risk, and apply Zero Trust segmentation where patching cannot happen immediately."
      },
      {
        "kind": "h4",
        "text": "Read More — Cisco Talos Source References"
      },
      {
        "kind": "bullet",
        "text": "**The Closed Quorum: Inside the first reported autonomous AI C2 implant** [add link]"
      },
      {
        "kind": "bullet",
        "text": "**Introducing CAIRN: Frontier tracking for AI-integrated malware** [add link]"
      },
      {
        "kind": "bullet",
        "text": "**ArcaneDoor: New espionage-focused campaign targeting perimeter network devices** [add link]"
      },
      {
        "kind": "bullet",
        "text": "**Cisco Talos Vulnerability Reports** [add link]"
      },
      {
        "kind": "bullet",
        "text": "**Snort Rule Documentation** [add link]"
      }
    ]
  },
  {
    "id": "appendix-overview",
    "number": 5,
    "title": "APPENDIX: Cisco Solutions and Product Architecture Portfolio",
    "blocks": [
      {
        "kind": "p",
        "text": "Detailed architectural overviews, technical capabilities, and solution deep dives for the core Cisco platforms referenced throughout the workshop recommendations. Together these form the Cisco Security Cloud and Resilient Infrastructure architecture designed to defend modern enterprises against AI-era cyber threats."
      },
      {
        "kind": "p",
        "text": "A.1 Zero Trust Network Access & Continuous Identity Resilience\nA.2 Hybrid Mesh Microsegmentation & Advanced Threat Protection\nA.3 Unified Asset & Lifecycle Intelligence\nA.4 Cisco Live Protect & Shield — Continuous Defense Resiliency\nA.5 Cisco AI Defense — Securing Enterprise LLMs & Autonomous Agents\nA.6 Reference & Industry Benchmarks"
      }
    ]
  },
  {
    "id": "recommended-immediate-next",
    "number": 6,
    "title": "Appendix 1: Zero Trust Network Access & Continuous Identity Resilience",
    "blocks": [
      {
        "kind": "p",
        "text": "**Key Cisco Products:** Cisco Secure Access & Duo Security\nIn the AI era, attackers leverage generative AI for credential synthesis, automated session theft, and convincing deepfakes. Cisco delivers a unified Zero Trust architecture that decouples access from network location, enforces least privilege per application request, and verifies identity continuously."
      },
      {
        "kind": "h4",
        "text": "Universal ZTNA Gateway — Cisco Secure Access"
      },
      {
        "kind": "p",
        "text": "Seamless, high-performance least-privilege access to private applications, SaaS, and the internet through a single cloud platform, deprecating broad-access legacy VPNs and stopping lateral movement before it starts."
      },
      {
        "kind": "h4",
        "text": "Continuous Identity Verification — Duo Security"
      },
      {
        "kind": "p",
        "text": "Evaluates user identity, device health, and behavioral risk at every authentication event. Features passwordless FIDO2 biometrics, deepfake-resistant push verification, and dynamic risk-based step-up authentication."
      },
      {
        "kind": "h4",
        "text": "Cisco Identity Intelligence"
      },
      {
        "kind": "p",
        "text": "Unifies identity graphs across enterprise identity providers (Okta, Microsoft Entra, Active Directory), exposing dormant accounts, shadow privileges, and compromised credentials before adversaries can exploit them."
      },
      {
        "kind": "h3",
        "text": "Universal Zero Trust & Identity Architecture"
      },
      {
        "kind": "p",
        "text": "More information can be found: [add link]."
      }
    ]
  },
  {
    "id": "resources-tool-directory",
    "number": 7,
    "title": "Appendix 2: Hybrid Mesh Microsegmentation & Advanced Threat Protection",
    "blocks": [
      {
        "kind": "p",
        "text": "**Key Cisco Products:** Cisco Secure Firewall & Cisco Secure Network Analytics\nFlat networks are the primary enabler of ransomware propagation and AI-driven lateral attack traversal. Cisco combines next-generation firewall enforcement with network flow telemetry to create an adaptive Hybrid Mesh Firewall architecture spanning campus, data center, branch, and OT environments."
      },
      {
        "kind": "h4",
        "text": "Cisco Secure Firewall — Threat Defense"
      },
      {
        "kind": "p",
        "text": "Granular Layer 7 application visibility and industry-leading Snort 3 intrusion prevention across physical appliances, virtual instances, and cloud-native fabrics, with hardware-accelerated encrypted traffic visibility and no full-decryption overhead."
      },
      {
        "kind": "h4",
        "text": "Cisco Secure Network Analytics"
      },
      {
        "kind": "p",
        "text": "Uses NetFlow, IPFIX, and telemetry from Cisco switches, routers, and firewalls to baseline normal operational behavior, detect unauthorized east-west communication, and identify anomalous data staging in real time."
      },
      {
        "kind": "h4",
        "text": "Industrial & OT Security Segmentation"
      },
      {
        "kind": "p",
        "text": "Secures industrial Ethernet switches, SCADA systems, and manufacturing operations by enforcing Zero Trust microsegmentation policies directly at the access layer without requiring downtime."
      },
      {
        "kind": "h3",
        "text": "Hybrid Mesh Microsegmentation & Threat Protection"
      },
      {
        "kind": "p",
        "text": "More information can be found: [add link]."
      }
    ]
  },
  {
    "id": "appendix-3-unified-asset",
    "number": 8,
    "title": "Appendix 3: Unified Asset & Lifecycle Intelligence",
    "blocks": [
      {
        "kind": "p",
        "text": "**Key Cisco products:** Cisco IQ, Cisco IQ Link & Cisco Cloud Control\nYou cannot secure what you cannot see. Fragmented spreadsheets and isolated management portals leave blind spots that adversaries exploit. Cisco IQ aggregates 100% of your Cisco deployed estate — including unmanaged gear, unassigned PO contracts, and active network nodes — into a single operational source of truth."
      },
      {
        "kind": "h4",
        "text": "100% Deployed Estate Visibility"
      },
      {
        "kind": "p",
        "text": "Consolidates hardware, software, contract coverage, and support status across Catalyst Center, Catalyst SD-WAN Manager, Nexus Dashboard, and Meraki Dashboard into a unified cloud-native console."
      },
      {
        "kind": "h4",
        "text": "Automated PSIRT & Field Notice Mapping"
      },
      {
        "kind": "p",
        "text": "Automatically matches newly published security advisories and hardware Field Notices against your exact product IDs and software releases, quantifying risk and eliminating manual spreadsheet tracking."
      },
      {
        "kind": "h4",
        "text": "Cisco IQ Link & On-Premises Collectors"
      },
      {
        "kind": "p",
        "text": "Enables secure, lightweight on-premises telemetry extraction from isolated campus and data center segments, feeding real-time device health and configuration data to Cisco IQ."
      },
      {
        "kind": "h3",
        "text": "Unifying Asset Telemetry & Delivering Proactive Customer Value"
      },
      {
        "kind": "p",
        "text": "More information can be found: [add link]."
      }
    ]
  },
  {
    "id": "appendix-4-live-protect",
    "number": 9,
    "title": "Appendix 4: Cisco Live Protect & Shield - Continuous Defense Resiliency",
    "blocks": [
      {
        "kind": "p",
        "text": "**Key Cisco products:** Cisco Resilient Infrastructure Services, Cisco Live Protect & Shield\nPoint-in-time assessments quickly become obsolete in dynamic IT environments. Cisco Resilient Infrastructure Services provides an ongoing, agentic operational support framework combining continuous monitoring, automated vulnerability validation, and specialized engineering delivery capacity."
      },
      {
        "kind": "h4",
        "text": "Cisco Live Protect with Shield Integration"
      },
      {
        "kind": "p",
        "text": "Continuously audits configuration drift, posture anomalies, and security policy adherence across your estate, using Shield real-time telemetry to trigger automated remediation workflows before vulnerabilities are weaponized."
      },
      {
        "kind": "h4",
        "text": "Automated Validation Framework (AVF)"
      },
      {
        "kind": "p",
        "text": "Simulates changes, upgrades, and segmentation policies in software-defined digital twins prior to production execution, ensuring no unexpected outages or dependency breaks during large-scale modernizations."
      },
      {
        "kind": "h4",
        "text": "Services As Code & Expert Delivery"
      },
      {
        "kind": "p",
        "text": "Cisco Professional Services provides turnkey execution capacity using programmatic deployment playbooks to accelerate firmware upgrades, firewall migrations, and microsegmentation rollouts."
      },
      {
        "kind": "h3",
        "text": "Cisco Live Protect & Shield Continuous Defense Resiliency"
      },
      {
        "kind": "p",
        "text": "More information can be found: [add link]."
      }
    ]
  },
  {
    "id": "appendix-5-ai-defense",
    "number": 10,
    "title": "Appendix 5: Cisco AI Defense - Securing Enterprise LLMs & Autonomous Agents",
    "blocks": [
      {
        "kind": "p",
        "text": "**Key Cisco products:** Cisco AI Defense, Cisco Secure Access, Duo & Splunk\nEnterprise adoption of generative AI, internal LLMs, coding copilots, and autonomous agent workflows introduces new attack surfaces including prompt injection, model jailbreaks, data exfiltration, and unauthorized tool execution. Cisco AI Defense provides comprehensive runtime security across the entire AI application lifecycle."
      },
      {
        "kind": "h4",
        "text": "Runtime AI Guardrails & Prompt Protection"
      },
      {
        "kind": "p",
        "text": "Inspects inbound user prompts and model responses in real time, detecting and blocking adversarial prompt injection, jailbreak attempts, toxic content, and sensitive proprietary data leakage."
      },
      {
        "kind": "h4",
        "text": "Autonomous Agent & MCP Tool Scoping"
      },
      {
        "kind": "p",
        "text": "Enforces strict authorization and least-privilege policies on Model Context Protocol servers and agent tool invocation APIs, preventing hijacked agents from performing destructive unauthorized actions."
      },
      {
        "kind": "h4",
        "text": "Emergency Kill-Switch & Splunk Telemetry"
      },
      {
        "kind": "p",
        "text": "Provides granular administrative kill-switch controls to instantly isolate rogue agents or compromised AI models, while streaming full inference logs and prompt telemetry to Splunk Enterprise Security for continuous threat hunting."
      },
      {
        "kind": "h3",
        "text": "Cisco AI Defense: Securing Enterprise LLMs & Autonomous Agents"
      },
      {
        "kind": "p",
        "text": "More information can be found: [add link]."
      }
    ]
  },
  {
    "id": "appendix-6-references",
    "number": 11,
    "title": "Appendix 6: References & Industry Benchmarks",
    "blocks": [
      {
        "kind": "p",
        "text": "Authoritative research publications, threat intelligence reports, and industry benchmarks cited throughout this workshop report to guide risk prioritization, vulnerability management, and infrastructure resilience."
      },
      {
        "kind": "h4",
        "text": "Gartner Research — Emerging Risk Report & Strategic Cybersecurity Trends"
      },
      {
        "kind": "p",
        "text": "Publication: Gartner Quarterly Emerging Risk Report / Gartner Newsroom. Key Insight: Gartner ranked AI-enabled vulnerability discovery as the top emerging risk in Q2 2026, noting that AI increases both the efficiency and accessibility of finding and weaponizing vulnerabilities. The report recommends recalibrating risk assessments and accelerating cyber response strategies, including more automated remediation."
      },
      {
        "kind": "bullet",
        "text": "Gartner: AI Discovery of Cyber Vulnerabilities is Top Emerging Risk — [add link]"
      },
      {
        "kind": "bullet",
        "text": "Gartner Cybersecurity Insights — [add link]"
      },
      {
        "kind": "h4",
        "text": "Forrester Research — Technology & Security Predictions / Technical Debt Risk"
      },
      {
        "kind": "p",
        "text": "Publication: Forrester Technology & Security Predictions 2025 / Forrester Predictions Hub. Key Insight: Forrester projects that technical debt will become a moderate-to-high severity issue for many technology decision-makers, driven in part by accelerated AI adoption and IT complexity. The implication for vulnerability management is that organizations must reduce legacy exposure, improve visibility, and use risk-based prioritization rather than relying on manual patch queues."
      },
      {
        "kind": "bullet",
        "text": "Forrester: Technology & Security Predictions 2025 — [add link]"
      },
      {
        "kind": "bullet",
        "text": "Forrester Predictions Hub — [add link]"
      },
      {
        "kind": "h4",
        "text": "Cisco Talos Threat Intelligence — Real-World AI Malware, Vulnerability Research, and Protections"
      },
      {
        "kind": "p",
        "text": "Publication: Cisco Talos CLOSEDQUORUM analysis, CAIRN research, ArcaneDoor investigation, Talos Vulnerability Reports, and Snort rule documentation. Key Insight: Talos documented CLOSEDQUORUM as an autonomous AI C2 implant pattern, introduced CAIRN to identify cognitive artifacts in AI-integrated malware, and investigated ArcaneDoor as a real-world exploitation campaign against perimeter network devices. These findings illustrate why customers must combine rapid patching, Snort 3 IPS protections, Cisco IQ exposure correlation, forensic readiness, MFA, and Zero Trust segmentation."
      },
      {
        "kind": "bullet",
        "text": "Talos: CLOSEDQUORUM autonomous AI C2 implant — [add link]"
      },
      {
        "kind": "bullet",
        "text": "Talos: Introducing CAIRN — [add link]"
      },
      {
        "kind": "bullet",
        "text": "Talos: ArcaneDoor campaign targeting perimeter network devices — [add link]"
      },
      {
        "kind": "bullet",
        "text": "Cisco Talos Vulnerability Reports — [add link]"
      },
      {
        "kind": "bullet",
        "text": "Snort Rule Documentation — [add link]"
      },
      {
        "kind": "h4",
        "text": "EY — Global Cybersecurity Leadership Insights / Frontier AI Resilience"
      },
      {
        "kind": "p",
        "text": "Publication: EY Global Cybersecurity Leadership Insights / Frontier AI cybersecurity resilience. Key Insight: EY highlights that frontier AI changes vulnerability management by allowing systems to reason across software and combine multiple weaknesses into credible end-to-end compromise paths in minutes or hours. EY also identifies a visibility and control gap across enterprise assets, reinforcing the need for continuous validation, governance, and security control evidence."
      },
      {
        "kind": "bullet",
        "text": "EY: Building resilience in the age of frontier AI — [add link]"
      },
      {
        "kind": "bullet",
        "text": "EY Cybersecurity Insights — [add link]"
      },
      {
        "kind": "bullet",
        "text": "EY Technology Risk — [add link]"
      },
      {
        "kind": "h4",
        "text": "World Economic Forum (WEF) & CISA — Global Cybersecurity Outlook and KEV Catalog"
      },
      {
        "kind": "p",
        "text": "Publication: WEF Global Cybersecurity Outlook 2025 and CISA Known Exploited Vulnerabilities Catalog. Key Insight: WEF emphasizes growing systemic cyber risk from geopolitical tension, emerging technologies, and ecosystem dependency, while CISA KEV provides an authoritative catalog of vulnerabilities known to be exploited in the wild. Together, these sources support exploit-aware prioritization and continuous remediation governance."
      },
      {
        "kind": "bullet",
        "text": "WEF Global Cybersecurity Outlook 2025 — [add link]"
      },
      {
        "kind": "bullet",
        "text": "CISA Known Exploited Vulnerabilities Catalog — [add link]"
      },
      {
        "kind": "h3",
        "text": "Recommended Next Step"
      },
      {
        "kind": "p",
        "text": "> Within the next two weeks, [Customer Company Name], the account team, and the Security SE should assign an accountable owner to each priority and confirm the proposed 30, 60, and 90-day milestones.\nThe first working sessions should address Cisco IQ onboarding, validation of high-risk unsupported assets, production segmentation, and discovery of Shadow AI. This provides an immediate path to reduce exposure while establishing a sustainable operating model for visibility, modernization, segmentation, identity, and AI workload security."
      }
    ]
  }
];

/**
 * The report as it is assembled today: all 10 sections of the ACME Global
 * Enterprises reference report (Executive Summary through Appendix 6). Every
 * section renders on screen, in the PDF, and in the Word export from this
 * same authored text (see modules/report/assemble.ts), so the three outputs
 * cannot drift into three different documents. None of sections 3-10 has a
 * physical .docx heading in the currently-uploaded template asset, so
 * `modules/export/reportTemplate.ts` deliberately narrows what it hands
 * `modules/report/docx.ts` as the section list to search headings for (see
 * `DOCX_ANCHORED_SECTION_IDS` there): a section search that finds no range
 * for any of them falls through to `customXml`'s "not in the template" path,
 * appending each at the end of the document instead of being silently
 * dropped — correct for these, since there is nowhere in the physical file
 * to splice them into.
 */
export const ACTIVE_REPORT_TEMPLATE: TemplateSection[] = REPORT_TEMPLATE.filter((section) =>
  [
    'executive-summary',
    'key-workshop-findings',
    'post-workshop-action',
    'post-workshop-recommendations',
    'appendix-overview',
    'recommended-immediate-next',
    'resources-tool-directory',
    'appendix-3-unified-asset',
    'appendix-4-live-protect',
    'appendix-5-ai-defense',
    'appendix-6-references',
  ].includes(section.id),
);
